/**
 * Performance Monitor Hub
 * 
 * This file provides a unified interface for performance monitoring,
 * gracefully handling both older and newer implementations.
 */

// Import both performance monitor implementations
import { performanceMonitor } from './performanceMonitor';
import apiPerformanceMonitor from './apiPerformanceMonitor';
import simpleApiMonitor from './simpleApiMonitor';

/**
 * Unified performance monitoring interface
 */
const performanceHub = {
  /**
   * Initialize all performance monitoring systems
   */
  initialize() {
    // Array to collect cleanup functions
    const cleanupFunctions = [];

    try {
      // Start main performance monitoring
      console.log('Initializing core performance monitoring...');
      if (typeof performanceMonitor?.startTracking === 'function') {
        performanceMonitor.startTracking();
        cleanupFunctions.push(() => {
          try {
            performanceMonitor.stopTracking();
          } catch (err) {
            console.error('Error stopping performance tracking:', err);
          }
        });
      }

      // Initialize API performance monitoring - try main monitor first
      console.log('Initializing API performance monitoring...');
      try {
        if (typeof apiPerformanceMonitor?.initialize === 'function') {
          const cleanup = apiPerformanceMonitor.initialize();
          if (typeof cleanup === 'function') {
            cleanupFunctions.push(cleanup);
          }
          console.log('Using primary API performance monitor');
        }
      } catch (apiMonitorError) {
        console.warn('Primary API monitor failed, trying backup:', apiMonitorError);
        
        // If primary monitor fails, try the simplified monitor
        try {
          if (typeof simpleApiMonitor?.initialize === 'function') {
            const backupCleanup = simpleApiMonitor.initialize();
            if (typeof backupCleanup === 'function') {
              cleanupFunctions.push(backupCleanup);
            }
            console.log('Using backup API performance monitor');
          }
        } catch (backupError) {
          console.error('All API monitoring systems failed:', backupError);
        }
      }

      console.log('🚀 Performance Hub initialized successfully');
    } catch (error) {
      console.error('Error initializing performance hub:', error);
    }

    // Return a unified cleanup function
    return () => {
      cleanupFunctions.forEach(cleanup => {
        try {
          if (typeof cleanup === 'function') {
            cleanup();
          }
        } catch (err) {
          console.error('Error in cleanup:', err);
        }
      });
    };
  },

  /**
   * Get all performance metrics from both monitoring systems
   */
  getAllMetrics() {
    const metrics = {
      core: {},
      api: {}
    };

    // Get core metrics
    try {
      if (performanceMonitor && typeof performanceMonitor.getMetrics === 'function') {
        metrics.core = performanceMonitor.getMetrics();
      }
    } catch (err) {
      console.error('Error getting core metrics:', err);
    }

    // Try to get API metrics from both systems
    try {
      if (apiPerformanceMonitor && typeof apiPerformanceMonitor.getApiStats === 'function') {
        metrics.api.primary = apiPerformanceMonitor.getApiStats();
      }
    } catch (err) {
      console.warn('Error getting primary API metrics:', err);
    }

    try {
      if (simpleApiMonitor && typeof simpleApiMonitor.generateReport === 'function') {
        // Note: this would normally output to console, but metrics are also accessible
        simpleApiMonitor.generateReport();
      }
    } catch (err) {
      console.warn('Error generating simple API report:', err);
    }

    return metrics;
  },

  /**
   * Generate a comprehensive performance report
   */
  generateReport() {
    console.group('📊 Performance Hub - Comprehensive Report');
    
    try {
      // Core metrics
      console.group('Core Performance Metrics');
      if (performanceMonitor && typeof performanceMonitor.logSummary === 'function') {
        performanceMonitor.logSummary();
      } else {
        console.log('Core performance metrics not available');
      }
      console.groupEnd();

      // API metrics - try both systems
      console.group('API Performance Metrics');
      let apiReportGenerated = false;
      
      try {
        if (apiPerformanceMonitor && typeof apiPerformanceMonitor.logApiPerformanceReport === 'function') {
          apiPerformanceMonitor.logApiPerformanceReport();
          apiReportGenerated = true;
        }
      } catch (err) {
        console.warn('Error generating primary API report:', err);
      }
      
      if (!apiReportGenerated) {
        try {
          if (simpleApiMonitor && typeof simpleApiMonitor.generateReport === 'function') {
            simpleApiMonitor.generateReport();
            apiReportGenerated = true;
          }
        } catch (err) {
          console.warn('Error generating backup API report:', err);
        }
      }
      
      if (!apiReportGenerated) {
        console.log('No API performance metrics available');
      }
      
      console.groupEnd();
    } catch (err) {
      console.error('Error generating performance report:', err);
    }
    
    console.groupEnd();
  }
};

export default performanceHub;